import { useSelector } from "react-redux"

import { DEAL_TYPE } from "components/RaiseTicketForm/config/projectFormConstants"
import { TStore } from "store/Store"

/**
 * Statiq CPO deals always carry the same handful of values, so the raise form prefills them instead
 * of making ops retype them on every project (SB-8432).
 *
 * Two rules hold for every default in here: a saved value always wins over the default, and the
 * field stays editable afterwards. That is what lets the same prefill run on both add and edit.
 */

export const CPO_DEFAULT_OCPP_URL = "ws://ocpp.statiq.in"
export const CPO_DEFAULT_PORT_NUMBER = "80"

export const CPO_DEFAULT_PROJECT_CATEGORY = "Statiq CPO"
export const FRANCHISE_PROJECT_CATEGORY = "Franchise"

export const CPO_DEFAULT_CSMS_PARTNER = "statiq"

export const isCpoDealType = (dealTypeTitle?: string) => dealTypeTitle === DEAL_TYPE.CPO_TITLE

/**
 * Account id of Statiq's own account, which Buyer and CPO default to. It is configured per
 * environment (REACT_APP_STATIQ_ACCOUNT_ID) because the id differs between dev, SIT and production.
 *
 * Returns null when the variable is unset or not a usable id, which leaves the two accounts empty
 * for the user to pick rather than prefilling whichever account happens to hold that id.
 */
export const getStatiqAccountId = (): number | null => {
    const configuredId = Number(process.env.REACT_APP_STATIQ_ACCOUNT_ID)
    return Number.isInteger(configuredId) && configuredId > 0 ? configuredId : null
}

/**
 * A CPO project is a Franchise project when the franchise checkbox is ticked or the buyer is itself
 * a franchise account; otherwise it is a plain Statiq CPO project.
 */
export const resolveCpoProjectCategory = (isFranchisePartner: boolean, buyerAccountName?: string) =>
    isFranchisePartner || buyerAccountName?.toLowerCase().includes(FRANCHISE_PROJECT_CATEGORY.toLowerCase())
        ? FRANCHISE_PROJECT_CATEGORY
        : CPO_DEFAULT_PROJECT_CATEGORY

/** True while the project form is raising/editing a Statiq CPO deal. */
const useIsCpoDeal = () => {
    const dealTypeTitle = useSelector((state: TStore) => state.ProjectForm.deal_details_form?.deal_type?.title)
    return isCpoDealType(dealTypeTitle)
}

export default useIsCpoDeal
